import { useState, useEffect, useContext } from "react";
import {
  Layout,
  theme,
  Button,
  Select,
  Card,
  Row,
  Col,
  Table,
  Tag,
  Statistic,
  Tooltip,
  Drawer,
  Spin,
  Modal,
  Form,
  InputNumber,
  Input,
} from "antd";
import {
  ReconciliationOutlined,
  PlayCircleOutlined,
  SelectOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  BarChartOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import LoadingOverlay from "react-loading-overlay-ts";
import dayjs from "dayjs";

import AdminTopBar from "../../components/TopBar/AdminTopbar";
import AdminSideBar from "../../components/SideBar/AdminSidebar";

import { getGroupsApi, getBalanceReconciliationApi, fixPolicyBalanceApi } from "../../services/Admin/AdminApis";
import { getMemberTransactionsApi, addAdjustmentApi } from "../../services/Transaction/TransactionApis";
import { getMemberApi } from "../../services/Member/MemberApis";
import { AuthContext } from "../../context/AuthContext";

const { Content } = Layout;

const AdminReconPage: React.FC = () => {
  const [isActive, setActive] = useState(true);
  const [running, setRunning] = useState(false);
  const [groups, setGroups] = useState<any[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [reconResult, setReconResult] = useState<any>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState<any>(null);
  const [drawerMember, setDrawerMember] = useState<any>(null);
  const [drawerTxns, setDrawerTxns] = useState<any[]>([]);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustForm] = Form.useForm();
  const [fixLoading, setFixLoading] = useState(false);
  const [searchText, setSearchText] = useState("");

  const { currentUser } = useContext(AuthContext);

  const {
    token: { colorBgContainer },
  } = theme.useToken();

  useEffect(() => {
    getGroupsApi()
      .then((res) => {
        setGroups(res.data || []);
      })
      .catch((err) => console.log(err))
      .finally(() => setActive(false));
  }, []);

  const handleRunRecon = async () => {
    if (!selectedGroupId) return;
    setRunning(true);
    setReconResult(null);
    setSearchText("");
    getBalanceReconciliationApi(selectedGroupId)
      .then((res) => {
        setReconResult(res.data);
      })
      .catch((err) => console.log(err))
      .finally(() => setRunning(false));
  };

  const openStatement = (record: any) => {
    setDrawerRecord(record);
    setDrawerMember(null);
    setDrawerTxns([]);
    setDrawerOpen(true);
    setDrawerLoading(true);
    refreshDrawerData(record);
  };

  const refreshDrawerData = (record: any) => {
    setDrawerLoading(true);
    Promise.all([
      getMemberTransactionsApi(String(record.memberId)),
      getMemberApi(String(record.memberId)),
    ])
      .then(([txnRes, memberRes]) => {
        const sorted = (txnRes.data || []).sort(
          (a: any, b: any) => new Date(b.Date).getTime() - new Date(a.Date).getTime()
        );
        setDrawerTxns(sorted);
        setDrawerMember(memberRes.data || null);

        const calculatedBalance = sorted.reduce((sum: number, t: any) => {
          const amount = Number(t.Amount);
          // Refund-type adjustments were historically stored as positive but represent debits
          if (t.Type === "Refund" && amount > 0) return sum - amount;
          return sum + amount;
        }, 0);
        const difference = calculatedBalance - Number(record.savedBalance);
        setDrawerRecord((prev: any) => ({
          ...prev,
          calculatedBalance: calculatedBalance.toFixed(2),
          difference: difference.toFixed(2),
        }));
      })
      .catch((err) => console.log(err))
      .finally(() => setDrawerLoading(false));
  };

  const handleFixBalance = () => {
    if (!drawerRecord?.policyId) return;
    setFixLoading(true);
    fixPolicyBalanceApi(String(drawerRecord.policyId))
      .then((res) => {
        const data = res.data;
        setDrawerRecord((prev: any) => ({
          ...prev,
          savedBalance: data.newBalance,
          difference: "0",
        }));
        refreshDrawerData(drawerRecord);
      })
      .catch((err) => console.log(err))
      .finally(() => setFixLoading(false));
  };

  const handleAdjustSubmit = () => {
    adjustForm.validateFields().then((values) => {
      setAdjustLoading(true);
      const signedAmount = values.direction === "credit"
        ? Math.abs(values.amount)
        : -Math.abs(values.amount);
      addAdjustmentApi(drawerRecord.policyId, {
        amount: signedAmount,
        note: values.note,
        userId: currentUser.id,
      })
        .then(() => {
          setAdjustOpen(false);
          adjustForm.resetFields();
          refreshDrawerData(drawerRecord);
        })
        .catch((err) => console.log(err))
        .finally(() => setAdjustLoading(false));
    });
  };

  const txnColumns = [
    {
      title: "Date",
      dataIndex: "Date",
      render: (val: string) => val ? dayjs(val).format("YYYY-MM-DD") : "-",
    },
    {
      title: "Type",
      dataIndex: "Type",
    },
    {
      title: "Reference",
      dataIndex: "Reference",
    },
    {
      title: "Note",
      dataIndex: "Note",
      render: (val: string) => val || "-",
    },
    {
      title: "Debit (R)",
      dataIndex: "Amount",
      render: (val: string, record: any) => {
        const num = Number(val);
        const isDebit = num < 0 || record.Type === "Refund";
        return isDebit ? (
          <span style={{ color: "#cf1322", fontWeight: 600 }}>R{Math.abs(num).toFixed(2)}</span>
        ) : "-";
      },
    },
    {
      title: "Credit (R)",
      dataIndex: "Amount",
      render: (val: string, record: any) => {
        const num = Number(val);
        const isCredit = num > 0 && record.Type !== "Refund";
        return isCredit ? (
          <span style={{ color: "#389e0d", fontWeight: 600 }}>R{num.toFixed(2)}</span>
        ) : "-";
      },
    },
  ];

  const columns = [
    {
      title: "Policy Number",
      dataIndex: "policyNumber",
      sorter: (a: any, b: any) =>
        a.policyNumber.localeCompare(b.policyNumber),
    },
    {
      title: "Member",
      dataIndex: "memberName",
      sorter: (a: any, b: any) => a.memberName.localeCompare(b.memberName),
    },
    {
      title: "Group",
      dataIndex: "groupName",
    },
    {
      title: "Saved Balance (R)",
      dataIndex: "savedBalance",
      render: (val: string) => {
        const n = Number(val);
        return (
          <span style={{ color: n >= 0 ? "#389e0d" : "#cf1322" }}>
            R{Number(val).toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Calculated Balance (R)",
      dataIndex: "calculatedBalance",
      render: (val: string) => {
        const n = Number(val);
        return (
          <span style={{ color: n >= 0 ? "#389e0d" : "#cf1322" }}>
            R{Number(val).toFixed(2)}
          </span>
        );
      },
    },
    {
      title: "Difference (R)",
      dataIndex: "difference",
      sorter: (a: any, b: any) =>
        Math.abs(Number(a.difference)) - Math.abs(Number(b.difference)),
      defaultSortOrder: "descend" as const,
      render: (val: string) => (
        <Tag color={Number(val) === 0 ? "green" : Math.abs(Number(val)) > 50 ? "red" : "orange"}>
          R{Number(val).toFixed(2)}
        </Tag>
      ),
    },
    {
      title: "Transactions",
      dataIndex: "transactionCount",
    },
    {
      title: "Last Transaction",
      dataIndex: "lastTransactionDate",
      render: (val: string) =>
        val ? dayjs(val).format("YYYY-MM-DD") : "-",
    },
    {
      title: "Action",
      render: (_: any, record: any) => (
        <Tooltip title="View Account Statement">
          <Button
            size="small"
            icon={<SelectOutlined />}
            onClick={() => openStatement(record)}
          >
            Statement
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        <AdminSideBar />
        <Layout className="site-layout">
          <AdminTopBar />
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <h2>
                <ReconciliationOutlined
                  style={{ paddingRight: "5px", color: "#f04f05" }}
                />
                Balance Reconciliation
              </h2>

              <Card style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]} align="middle">
                  <Col xs={24} sm={12} md={8}>
                    <Select
                      placeholder="Select a group"
                      style={{ width: "100%" }}
                      size="large"
                      showSearch
                      optionFilterProp="label"
                      options={groups.map((g: any) => ({
                        value: g.id,
                        label: g.GroupName,
                      }))}
                      onChange={(val) => setSelectedGroupId(val)}
                    />
                  </Col>
                  <Col>
                    <Button
                      type="primary"
                      size="large"
                      loading={running}
                      disabled={!selectedGroupId}
                      onClick={handleRunRecon}
                      style={{ background: "#f04f05", borderColor: "#f04f05" }}
                      icon={<PlayCircleOutlined />}
                    >
                      Run Reconciliation
                    </Button>
                  </Col>
                </Row>
              </Card>

              {reconResult && (
                <>
                  <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={8}>
                      <Card>
                        <Statistic
                          title="Total Policies Checked"
                          value={reconResult.summary.totalPoliciesChecked}
                          prefix={<BarChartOutlined style={{ color: "#1890ff" }} />}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Card>
                        <Statistic
                          title="Discrepancies Found"
                          value={reconResult.summary.totalDiscrepancies}
                          prefix={<WarningOutlined style={{ color: "#fa8c16" }} />}
                          valueStyle={{ color: reconResult.summary.totalDiscrepancies > 0 ? "#fa8c16" : "#52c41a" }}
                        />
                      </Card>
                    </Col>
                    <Col xs={24} sm={8}>
                      <Card>
                        <Statistic
                          title="Accuracy"
                          value={reconResult.summary.percentageAccurate}
                          suffix="%"
                          prefix={<CheckCircleOutlined style={{ color: "#52c41a" }} />}
                          valueStyle={{ color: Number(reconResult.summary.percentageAccurate) >= 90 ? "#52c41a" : "#cf1322" }}
                        />
                      </Card>
                    </Col>
                  </Row>

                  <Input
                    placeholder="Search by policy number or member name…"
                    allowClear
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ marginBottom: 16, maxWidth: 360 }}
                  />

                  <Table
                    dataSource={(reconResult.discrepancies as any[]).filter((r) => {
                      const q = searchText.toLowerCase();
                      return (
                        r.policyNumber?.toLowerCase().includes(q) ||
                        r.memberName?.toLowerCase().includes(q)
                      );
                    })}
                    columns={columns}
                    rowKey="policyId"
                    pagination={{ pageSize: 20, hideOnSinglePage: true }}
                    scroll={{ x: true }}
                    rowClassName={(record) =>
                      Math.abs(Number(record.difference)) > 50
                        ? "ant-table-row-danger"
                        : ""
                    }
                  />
                </>
              )}
            </div>
          </Content>
        </Layout>
      </Layout>

      <Drawer
        title={
          drawerRecord
            ? `${drawerRecord.memberName} — ${drawerRecord.policyNumber}`
            : "Account Statement"
        }
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        width={900}
        destroyOnClose
        extra={
          <div style={{ display: "flex", gap: 8 }}>
            <Button
              onClick={handleFixBalance}
              loading={fixLoading}
              style={{ borderColor: "#1890ff", color: "#1890ff" }}
            >
              Sync Balance
            </Button>
            <Button
              icon={<PlusOutlined />}
              onClick={() => {
                adjustForm.resetFields();
                setAdjustOpen(true);
              }}
              style={{ background: "#f04f05", borderColor: "#f04f05", color: "#fff" }}
            >
              Add Adjustment
            </Button>
          </div>
        }
      >
        {drawerRecord && (
          <>
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Saved Balance"
                    value={`R${Number(drawerRecord.savedBalance).toFixed(2)}`}
                    valueStyle={{ color: Number(drawerRecord.savedBalance) >= 0 ? "#389e0d" : "#cf1322" }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Calculated Balance"
                    value={`R${Number(drawerRecord.calculatedBalance).toFixed(2)}`}
                    valueStyle={{ color: Number(drawerRecord.calculatedBalance) >= 0 ? "#389e0d" : "#cf1322" }}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  {(() => {
                    const diff = Number(drawerRecord.calculatedBalance) - Number(drawerRecord.savedBalance);
                    return (
                      <Statistic
                        title="Difference"
                        value={`R${diff.toFixed(2)}`}
                        valueStyle={{
                          color:
                            diff === 0
                              ? "#52c41a"
                              : Math.abs(diff) > 50
                              ? "#cf1322"
                              : "#fa8c16",
                        }}
                      />
                    );
                  })()}
                </Card>
              </Col>
            </Row>

            <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Inception Date"
                    value={drawerMember?.InceptionDate ? dayjs(drawerMember.InceptionDate).format("YYYY-MM-DD") : "-"}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Months Since Inception"
                    value={
                      drawerMember?.InceptionDate
                        ? dayjs().diff(dayjs(drawerMember.InceptionDate), "month")
                        : "-"
                    }
                    suffix={drawerMember?.InceptionDate ? "months" : ""}
                  />
                </Card>
              </Col>
              <Col span={8}>
                <Card size="small">
                  <Statistic
                    title="Monthly Premiums Listed"
                    value={drawerTxns.filter((t) => t.Type === "System" && t.Reference === "Premium Payable").length}
                  />
                </Card>
              </Col>
            </Row>

            {drawerLoading ? (
              <div style={{ textAlign: "center", padding: 40 }}>
                <Spin size="large" />
              </div>
            ) : (
              <Table
                dataSource={drawerTxns}
                columns={txnColumns}
                rowKey="id"
                size="small"
                pagination={false}
                summary={(pageData) => {
                  const totalDebit = pageData.reduce((sum, row: any) => {
                    const n = Number(row.Amount);
                    return sum + (n < 0 || row.Type === "Refund" ? Math.abs(n) : 0);
                  }, 0);
                  const totalCredit = pageData.reduce((sum, row: any) => {
                    const n = Number(row.Amount);
                    return sum + (n > 0 && row.Type !== "Refund" ? n : 0);
                  }, 0);
                  return (
                    <Table.Summary.Row style={{ fontWeight: 700, background: "#fafafa" }}>
                      <Table.Summary.Cell index={0} colSpan={4}>Total</Table.Summary.Cell>
                      <Table.Summary.Cell index={4}>
                        <span style={{ color: "#cf1322" }}>R{totalDebit.toFixed(2)}</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5}>
                        <span style={{ color: "#389e0d" }}>R{totalCredit.toFixed(2)}</span>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                  );
                }}
              />
            )}
          </>
        )}
      </Drawer>

      <Modal
        title="Add Adjustment Transaction"
        open={adjustOpen}
        onOk={handleAdjustSubmit}
        onCancel={() => { setAdjustOpen(false); adjustForm.resetFields(); }}
        confirmLoading={adjustLoading}
        okText="Submit Adjustment"
        zIndex={1100}
      >
        <Form form={adjustForm} layout="vertical">
          <Form.Item
            name="direction"
            label="Adjustment Type"
            rules={[{ required: true, message: "Select adjustment type" }]}
          >
            <Select
              placeholder="Select adjustment type"
              getPopupContainer={(trigger) => trigger.parentElement}
              options={[
                { value: "debit", label: "Credit (Positive — Charge Customer)" },
                { value: "credit", label: "Debit (Negative — Reduce Balance)" },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="amount"
            label="Amount (R)"
            rules={[{ required: true, message: "Enter amount" }]}
          >
            <InputNumber min={0.01} style={{ width: "100%" }} prefix="R" />
          </Form.Item>
          <Form.Item
            name="note"
            label="Explanation (required)"
            rules={[{ required: true, message: "An explanation is required for adjustments" }]}
          >
            <Input.TextArea rows={3} placeholder="Reason for adjustment..." />
          </Form.Item>
        </Form>
      </Modal>
    </LoadingOverlay>
  );
};

export default AdminReconPage;
